import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import { append, insert, insertAll, move, pick, remove, update } from 'ramda'
import React from 'react'
import { v1 as uuid } from 'uuid'

import IconText from '@/app/component/layout/icon-text'
import { cannot } from '@/app/helpers'
import ChoiceLabel from '@/app/module/campaigns/component/item/steps/content/question/choice/choice-label'
import { ChoiceListType, LocalAction, LocalActionType, LocalSpoken, SnippetType } from '@/app/module/campaigns/types'
import { generateActionFormValue } from '@/app/module/campaigns/utils/actions/form'
import { useSaveSnippet } from '@/app/module/user/hooks/use-save-snippet'

import ActionDialogWrapper from './action-dialog-wrapper'
import ActionList from './action-list'
import { ActionsBoxProps } from './actions-box-props'
import AddFromLibrary from './dialog/add-from-library'
import FinalActionsRow from './final-actions-row'

export type QuestionBoxValue = {
  label?: string
  choiceList?: ChoiceListType
  actionsList?: LocalAction[]
  speech?: boolean
  spoken?: LocalSpoken
}

type State = {
  addFromLibrarySource?: number
  dialogActive?: LocalActionType
  dialogSource?: number
  label?: string
}

type ActionsBoxInnerProps = ActionsBoxProps & {
  saveSnippet: (item: Pick<SnippetType, 'category' | 'snippet'>) => Promise<unknown>
}

class ActionsBoxInner extends React.PureComponent<ActionsBoxInnerProps, State> {
  constructor(props: ActionsBoxInnerProps) {
    super(props)

    this.state = {
      addFromLibrarySource: undefined,
      dialogActive: undefined,
      dialogSource: undefined,
      label: props.label,
    }
  }

  addToLibrary = async (action: LocalAction) => {
    const { saveSnippet } = this.props
    await saveSnippet({
      category: 'action',
      snippet: action,
    })
  }

  handleActionChange = (value: 'addFromLibrary' | LocalActionType, index: number) => {
    if (value === 'addFromLibrary') {
      this.setState({
        addFromLibrarySource: index,
      })

      return
    }

    const { actions, onChange } = this.props
    const finalActionTypes = this.props.actionOptions.filter((o) => o.final).map((o) => o.value)

    switch (value) {
      case LocalActionType.API:
      case LocalActionType.Pickup:
      case LocalActionType.Jump:
      case LocalActionType.PlayAudio:
      case LocalActionType.Reopen:
      case LocalActionType.SendEmail:
      case LocalActionType.SendSMS:
      case LocalActionType.SendSMSToPhoneNumbers:
      case LocalActionType.SendWhatsApp:
      case LocalActionType.SubscribeToCampaign:
      case LocalActionType.Topup:
      case LocalActionType.Transfer:
      case LocalActionType.UpdateContact:
        this.setState({
          dialogSource: index,
          dialogActive: value,
        })
        break

      case LocalActionType.FinishFlow:
      case LocalActionType.Hangup:
      case LocalActionType.Replay:
      case LocalActionType.Resend:
      case LocalActionType.RunQuota:
      case LocalActionType.UnsubscribeAll: {
        onChange(updateActionsAtIndex(actions, index, generateActionFormValue(value), finalActionTypes))
        break
      }

      case LocalActionType.Unsubscribe: {
        // do nothing as this action is not supported yet
        break
      }

      default:
        cannot(value)
        break
    }
  }

  handleActionMove = (from: number, to: number) => {
    const { actions, onChange } = this.props
    onChange(move(from, to, actions))
  }

  handleActionRemove = (index: number) => {
    const { actions, onChange } = this.props

    onChange(remove(index, 1, actions))
  }

  handleLabelChange = (label: string) => {
    this.setState({ label })
    this.props.onLabelChange?.(label)
  }

  renderAddFromLibraryDialog = () => {
    const { actionOptions, actions, personalizationList, onChange } = this.props
    const { addFromLibrarySource } = this.state

    return (
      <AddFromLibrary
        open={typeof addFromLibrarySource === 'number'}
        personalizationList={personalizationList}
        valuesList={actionOptions}
        onClose={() => {
          this.setState({
            addFromLibrarySource: undefined,
          })
        }}
        onInsert={(value = []) => {
          const newValue = value.map((item) => ({
            ...item,
            id: uuid(),
          }))
          this.setState(
            {
              addFromLibrarySource: undefined,
            },
            () => {
              const newActions = insertAll(addFromLibrarySource || 0, newValue, actions)

              onChange(newActions)
            },
          )
        }}
      />
    )
  }

  renderActionDialog = () => {
    const { actions, onChange } = this.props
    const { dialogActive, dialogSource } = this.state
    if (!dialogActive || typeof dialogSource !== 'number') {
      return null
    }

    const finalActionTypes = this.props.actionOptions.filter((o) => o.final).map((o) => o.value)

    const action = actions[dialogSource]
    // if current action type is different from selected dialog type, generate a new action with the dialog type
    const isNewAction = action?.type !== dialogActive
    const actionValue = isNewAction ? generateActionFormValue(dialogActive) : action

    const dialogProps = pick(
      [
        'countriesTimezones',
        'countryCode',
        'defaultLanguage',
        'defaultSenderId',
        'hasTranscribe',
        'imageId',
        'index',
        'languages',
        'personalizationList',
        'phoneNumber',
        'senderIds',
        'timezone',
        'timezones',
        'transcribeEnabled',
        'updateContactConfig',
        'voiceId',
        'getImageProps',
        'getVoiceProps',
      ],
      this.props,
    )

    return (
      <ActionDialogWrapper
        {...dialogProps}
        action={actionValue}
        isNewAction={isNewAction}
        onClose={() =>
          this.setState({
            dialogActive: undefined,
            dialogSource: undefined,
          })
        }
        onSave={(value: Omit<LocalAction, 'id' | 'type'>) => {
          const newAction = {
            id: actionValue.id,
            type: dialogActive,
            ...value,
          } as LocalAction
          this.setState(
            {
              dialogActive: undefined,
              dialogSource: undefined,
            },
            () => {
              const newActions = updateActionsAtIndex(actions, dialogSource, newAction, finalActionTypes)
              onChange(newActions)
            },
          )
        }}
      />
    )
  }

  render() {
    const {
      actionOptions,
      actions,
      children,
      continueLabel,
      disableLabel,
      files,
      hideFinalActions,
      info,
      isCloseDisabled,
      personalizationList,
      onClose,
    } = this.props
    const { label } = this.state

    const finalActionTypes = this.props.actionOptions
      .filter((o) => o.final)
      .reduce(
        (acc, curr) => ({
          ...acc,
          [curr.value]: true,
        }),
        {} as Record<LocalActionType, true>,
      )
    const hasFinalAction = actions.length > 0 && !!finalActionTypes[actions[actions.length - 1].type]
    const normalActions = hasFinalAction ? actions.slice(0, -1) : actions
    const finalAction = hasFinalAction ? actions[actions.length - 1] : undefined

    const finalActionOptions = actionOptions.filter((o) => o.final)

    const isClosable = typeof onClose === 'function'
    const hasLabel = !disableLabel

    return (
      <Container>
        {this.renderAddFromLibraryDialog()}
        {this.renderActionDialog()}
        {isClosable && (
          <HeaderRow sx={{ justifyContent: hasLabel ? 'space-between' : 'flex-end' }}>
            {hasLabel && (
              <LabelBox>
                <ChoiceLabel label={label || ''} onChange={this.handleLabelChange} />
              </LabelBox>
            )}
            <IconButton
              className="campaign-question-onAnswer-choice-delete-btn"
              data-testid="campaign-question-answer-delete-btn"
              onClick={onClose}
              disabled={isCloseDisabled}
              size="small"
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </HeaderRow>
        )}
        {children}
        <ActionList
          actionOptions={actionOptions}
          actions={normalActions}
          files={files}
          personalizationList={personalizationList}
          onActionAddToLibrary={this.addToLibrary}
          onActionChange={(value, index) => this.handleActionChange(value, index)}
          onActionMove={(from, to) => this.handleActionMove(from, to)}
          onActionRemove={(index) => this.handleActionRemove(index)}
        />

        {!hideFinalActions && (
          <FinalActionsRow
            actionOptions={finalActionOptions}
            continueLabel={continueLabel}
            files={files}
            finalAction={finalAction}
            personalizationList={personalizationList}
            onAdd={(type) => this.handleActionChange(type, normalActions.length)}
            onEdit={(type) => this.handleActionChange(type, normalActions.length)}
            onRemove={() => this.handleActionRemove(normalActions.length)}
          />
        )}
        {info && (
          <InfoWrapper>
            <InfoDivider />
            <IconText>
              <Icon>info</Icon>
              {info}
            </IconText>
          </InfoWrapper>
        )}
      </Container>
    )
  }
}

const updateActionsAtIndex = (
  actions: LocalAction[],
  index: number,
  action: LocalAction,
  finalActionTypes: LocalActionType[],
) => {
  // if index is equal to actions length, it means we are adding a new action at the end
  if (index === actions.length) {
    return append(action, actions)
  }

  const lastIndex = actions.length - 1
  const lastAction = actions[lastIndex]
  const hasFinalAtEnd = finalActionTypes.includes(lastAction.type as LocalActionType)
  const isFinalAction = finalActionTypes.includes(action.type as LocalActionType)

  // insert before the final action instead of replacing it
  if (hasFinalAtEnd && !isFinalAction && index === lastIndex) {
    return insert(index, action, actions)
  }
  return update(index, action, actions)
}

const ActionsBox: React.FC<ActionsBoxProps> = (props) => {
  const [saveSnippetMutation] = useSaveSnippet()

  const saveSnippet = React.useCallback(
    (item: Pick<SnippetType, 'category' | 'snippet'>) => {
      return saveSnippetMutation({ item })
    },
    [saveSnippetMutation],
  )

  return <ActionsBoxInner {...props} saveSnippet={saveSnippet} />
}

export default ActionsBox

const Container = styled('div')({
  backgroundColor: 'rgb(245, 245, 245)',
  padding: '12px',
  margin: '12px 0',
})

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
})

const LabelBox = styled('div')({
  height: '32px',
})

const InfoWrapper = styled('div')({
  color: 'rgba(0, 0, 0, 0.54)',
  padding: '0.35rem 0',
})

const InfoDivider = styled(Divider)({
  margin: '1rem -12px',
})
